import Head from 'next/head';
import Link from 'next/link';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AREAS = [
  {
    tag: 'Market structure',
    title: 'How positioning constrains price',
    body: 'The core research programme studies the mechanical behaviour of options markets: how dealer hedging obligations accumulate, where they concentrate, and how that positioning shapes what the underlying can do next. This is not pattern-matching on history. It is an attempt to measure obligations that market participants are required to meet.',
  },
  {
    tag: 'Applied signal processing',
    title: 'Instruments, not indicators',
    body: 'Work on how noisy, high-frequency market data can be resolved into stable structure. The output is a set of proprietary instruments the firm operates daily, refined continuously as conditions change. Methods that survive testing become part of the platform; methods that do not are retired and recorded as such.',
  },
  {
    tag: 'Quantum programme',
    title: 'A longer horizon',
    body: 'A research thrust in quantum methods for finance and molecular-scale design, with patents filed and pending across several application domains. This is deliberately long-dated work \u2014 the foundation for capabilities the firm expects to matter in years rather than quarters, and the connective tissue between our market research and our biotechnology interests.',
  },
];

export default function Research() {
  return (
    <>
      <Head>
        <title>Research &mdash; KTR Capital</title>
        <meta name="description" content="The KTR Capital research programme: market structure, applied signal processing, and quantum methods." />
        <meta name="robots" content="noindex" />
      </Head>
      <Navbar />
      <main className="bg-black text-white min-h-screen">

        <section className="px-6 pt-20 pb-14 border-b border-gray-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">Research</div>
            <h1 className="text-4xl md:text-5xl font-light leading-tight tracking-tight mb-6">
              We publish what we claim,<br />then grade it.
            </h1>
            <p className="text-lg text-gray-400 font-light leading-relaxed max-w-3xl">
              KTR Institute is the firm&rsquo;s research arm. Its work spans market microstructure, applied
              signal processing, and longer-horizon questions in quantum methods. The discipline that governs
              all of it is the same: a claim is dated before the outcome is known, and scored afterwards.
            </p>
          </div>
        </section>

        <section className="px-6 py-16 border-b border-gray-900">
          <div className="max-w-4xl mx-auto space-y-14">
            {AREAS.map((a, i) => (
              <div key={a.tag}>
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="text-xs text-orange-400 font-mono pt-1">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 mb-2">{a.tag}</div>
                    <h2 className="text-2xl md:text-3xl font-light tracking-tight">{a.title}</h2>
                  </div>
                </div>
                <p className="text-base text-gray-400 font-light leading-relaxed max-w-3xl md:pl-12">{a.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 py-16 border-b border-gray-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">The standard</div>
            <h2 className="text-2xl md:text-3xl font-light mb-6 tracking-tight">Dated in advance. Graded in public.</h2>
            <div className="space-y-5 text-base text-gray-400 font-light leading-relaxed max-w-3xl">
              <p>
                Research that cannot be checked is an opinion. The firm publishes a daily market read at
                helios.markets: issued in the morning, timestamped where anyone can see it, and scored against
                the close that evening &mdash; wins and losses alike, with nothing removed afterwards.
              </p>
              <p>
                We hold internal work to the same standard. A method is measured forward before it is trusted,
                and where a forward record runs below what testing suggested, we report the forward record. We
                quote no number the record has not earned.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light mb-4 tracking-tight">Going further</h2>
            <p className="text-base text-gray-400 font-light leading-relaxed max-w-3xl mb-8">
              Architecture, methodology and the detail behind this work are shared with qualified investors
              during the qualification process.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/sign-up" className="inline-block bg-orange-500 text-black px-6 py-3 rounded-lg font-medium text-sm hover:bg-orange-600 transition-colors">
                Begin qualification &rarr;
              </Link>
              <Link href="/platform" className="inline-block border border-gray-700 text-gray-200 px-6 py-3 rounded-lg font-light text-sm hover:bg-gray-900 transition-colors">
                How the platform is built
              </Link>
            </div>
            <p className="text-[11px] text-gray-600 font-light mt-10 max-w-3xl leading-relaxed">
              This page is informational and describes the firm&rsquo;s research activity. It is not an offer to
              sell, or a solicitation of an offer to buy, any security, and it is not a representation about
              future results. See{' '}
              <Link href="/disclosures" className="text-gray-500 hover:text-orange-400">Investor Disclosures</Link>.
            </p>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
